import React, { useState, useEffect } from "react";
import { Dropdown, Button, Spinner, Alert } from "react-bootstrap";
import { FaTrash, FaTimes } from "react-icons/fa";
import {
  addContent,
  getContents,
  updateContent,
  deleteContent,
  getRoomPlans,
  addPlanToRoom,
  updatePlanInRoom,
  deletePlanFromRoom,
  uploadImage,
} from "../../../FIREBASECONFIG";
import { AMENITY_OPTIONS } from "./Hotels";

const ResortRooms = ({ hotel }) => {
  const collectionName = "hotel_rooms";
  const imageFolder = "Room_Images";
  const [rooms, setRooms] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const itemsPerPage = 10;

  // UI State
  const [showForm, setShowForm] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [formData, setFormData] = useState({});
  const [gallery, setGallery] = useState([]); // [{url, alt, file}]
  const [plans, setPlans] = useState([]);

  // 🔹 Fetch Rooms
  const fetchRooms = async () => {
    try {
      setLoading(true);
      const data = await getContents(collectionName);
      const filtered = data.filter((r) => r.hotel_id === hotel.id);
      setRooms(filtered);
    } catch (error) {
      setAlert({ type: "danger", message: "Error fetching rooms." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [hotel]);

  // 🔹 Form Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleAmenity = (key) => {
    setFormData((prev) => {
      const current = prev.facilities || [];
      return {
        ...prev,
        facilities: current.includes(key)
          ? current.filter((a) => a !== key)
          : [...current, key],
      };
    });
  };

  // 🔹 Save Room + Plans
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 🔹 Upload images from gallery
      let uploadedImages = [];
      for (const g of gallery) {
        if (g.file) {
          const uploadedUrl = await uploadImage(g.file, imageFolder); 
          uploadedImages.push({ url: uploadedUrl, alt: g.alt });
        } else {
          uploadedImages.push({ url: g.url, alt: g.alt });
        }
      }
  
      // 🔹 Prepare room data
      const newRoom = {
        hotel_id: hotel.id,
        ...formData,
        facilities: formData.facilities || [],   // <-- from your form (checkbox/multiselect)
        images: uploadedImages, 
      };
  
      let roomId;
      if (editRoom) {
        await updateContent(collectionName, imageFolder, editRoom.id, newRoom);
        roomId = editRoom.id;
        setAlert({ type: "success", message: "Room updated successfully." });
      } else {
        roomId= await addContent(collectionName, imageFolder, newRoom);
        roomId=roomId.id;
        setAlert({ type: "success", message: "Room added successfully." });
      }
  
      // 🔹 Save Plans in Subcollection
      for (const plan of plans) {
        if (plan.id) {
          await updatePlanInRoom(roomId, plan.id, { ...plan, room_id: roomId, hotel_id: hotel.id });
        } else {
          await addPlanToRoom(roomId, { ...plan, room_id: roomId, hotel_id: hotel.id });
        }
      }
      // 🔹 Reset form
      setShowForm(false);
      setEditRoom(null);
      setFormData({});
      setGallery([]);
      setPlans([]);
      fetchRooms();
    } catch (error) {
      setAlert({ type: "danger", message: "Error saving room." });
    }
  };  
  // 🔹 Delete Room
  const handleDeleteRoom = async (id) => {
    try {
      await deleteContent(collectionName, id);
      setRooms((prev) => prev.filter((c) => c.id !== id));
      setAlert({ type: "success", message: "Room deleted successfully." });
    } catch (error) {
      setAlert({ type: "danger", message: "Error deleting room." });
    }
  };

  // 🔹 Images
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 10);
    const mapped = files.map((f) => ({
      file: f,
      url: URL.createObjectURL(f), // preview URL
      alt: "",
    }));
    setGallery((prev) => [...prev, ...mapped]);
  };

  const handleRemoveImage = (idx) => {
    setGallery((prev) => prev.filter((_, i) => i !== idx));
  };

  // 🔹 Plans
  const addPlan = () => {
    setPlans([
      ...plans,
      { name: "", maxPerson: "", refundable: false, stopSell: false, totalPlan: 0 },
    ]);
  };

  const updatePlan = (index, field, value) => {
    const updated = [...plans];
    updated[index][field] = value;
    setPlans(updated);
  };

  const deletePlan = async (index) => {
    const plan = plans[index];
    if (plan.id && editRoom) {
      await deletePlanFromRoom(editRoom.id, plan.id);
    }
    setPlans(plans.filter((_, i) => i !== index));
  };

  // 🔹 Edit Room (load its plans)
  const handleEditRoom = async (room) => {
    setEditRoom(room);
    setFormData(room);
    setGallery(
      room.images?.map((img) => ({
        url: img.url || img,
        alt: img.alt || "",
        file: null,
      })) || []
    );

    const roomPlans = await getRoomPlans(room.id);
    setPlans(roomPlans);
    setShowForm(true);
  };

  // 🔹 Filter + Paginate
  const filteredRooms = rooms.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredRooms.length / itemsPerPage);
  const paginatedRooms = filteredRooms.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );
  const handleToggle = async (room) => {
    try {
      const updated = { ...room, isApproved: !room.isApproved };
      await updateContent(collectionName, imageFolder, room.id, updated);
      setRooms((prev) => prev.map((r) => (r.id === room.id ? updated : r)));
      setAlert({ type: "success", message: `Room ${updated.isApproved ? "approved" : "unapproved"} successfully` });
    } catch (e) {
      console.error("Error updating Status:", e);
      setAlert({ type: "warning", message: `Error Updating IsApproved Status` });
    }
  };

  return (
    <div className="col-12">
       {alert && (
            <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
              {alert.message}
            </Alert>
        )}
      <div className="card">
        {/*---------- Header------------ */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0">Hotel Rooms</h4>
          {!showForm && (
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search room..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  setEditRoom(null);
                  setFormData({});
                  setGallery([]);
                  setPlans([]);
                  setShowForm(true);
                }}
                style={{ whiteSpace: "nowrap" }}
              >
                Add Room
              </Button>
            </div>
          )}
        </div>
        <div className="card-body">
          {showForm ? (
            // 🔹 Room Form
            <form onSubmit={handleSubmit}>
              {console.log(formData.id || "no rrom")}
              <div className="row">
                {/* Room Name */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Room Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name || ""}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                {/* Quantity */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    name="totalRoom"
                    value={formData.totalRoom || ""}
                    onChange={handleChange}
                    className="form-control"
                    required
                  />
                </div>

                {/* isApproved Toggle */}
                <div className="col-xl-6 mb-3">
                  <div className="form-check form-switch mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="isApproved"
                      style={{ cursor: "pointer" }}
                      checked={formData.isApproved || false}
                      onChange={handleChange}
                    />
                    <label className="form-check-label">Approved</label>
                  </div>
                </div>

                {/* Amenities */}
                <div className="col-xl-12 mb-3">
                  <label className="form-label">Facilities</label>
                  <div className="d-flex flex-wrap gap-2">
                    {AMENITY_OPTIONS.map(({ key, label, Icon }) => {
                      const active = formData.facilities?.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${active ? "btn-primary" : "btn-outline-primary"} d-flex align-items-center gap-2`}
                          onClick={() => toggleAmenity(key)}
                        >
                          <Icon />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Images */}
                <div className="col-xl-12 mb-3">
                  <label className="form-label">Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    className="form-control"
                  />
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {gallery.map((item, idx) => (
                      <div
                        key={idx}
                        className="position-relative border rounded p-1"
                        style={{ width: 130 }}
                      >
                        <img
                          src={item.url}
                          alt={item.alt || `img-${idx}`}
                          className="img-fluid rounded"
                          style={{ width: "100%", height: 80, objectFit: "cover" }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute"
                          style={{ top: 4, right: 4 }}
                          onClick={() => handleRemoveImage(idx)}
                          title="Remove image"
                        >
                          <FaTimes />
                        </button>
                        <input
                          type="text"
                          className="form-control form-control-sm mt-1"
                          placeholder="Alt text (required)"
                          required
                          value={item.alt || ""}
                          onChange={(e) => {
                            const updated = [...gallery];
                            updated[idx] = { ...item, alt: e.target.value };
                            setGallery(updated);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plans */}
                <div className="col-xl-12 mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label">Plans</label>
                    <Button size="sm" onClick={addPlan}>
                      + Add Plan
                    </Button>
                  </div>
                  {plans.map((plan, i) => (
                    <div key={i} className="d-flex gap-2 align-items-center mb-2">
                      <input
                        type="text"
                        placeholder="Name"
                        value={plan.name}
                        onChange={(e) => updatePlan(i, "name", e.target.value)}
                        className="form-control"
                      />
                      <input
                        type="number"
                        placeholder="Max Person"
                        value={plan.maxPerson}
                        onChange={(e) => updatePlan(i, "maxPerson", e.target.value)}
                        className="form-control"
                      />
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={plan.refundable}
                          onChange={(e) => updatePlan(i, "refundable", e.target.checked)}
                        />
                        <label className="form-check-label">Refundable</label>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          style={{ cursor: "pointer" }}
                          className="form-check-input"
                          checked={plan.stopSell}
                          onChange={(e) => updatePlan(i, "stopSell", e.target.checked)}
                        />
                        <label className="form-check-label">Stop Sell</label>
                      </div>
                      <input
                        type="number"
                        placeholder="Total Plan"
                        value={plan.totalPlan}
                        onChange={(e) => updatePlan(i, "totalPlan", e.target.value)}
                        className="form-control"
                      />
                      <Button variant="danger" size="sm" onClick={() => deletePlan(i)}>
                        <FaTrash />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Buttons */}
                <div className="col-12 d-flex justify-content-end">
                  <button type="submit" className="btn btn-secondary">
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary ms-2"
                    onClick={() => {
                      setShowForm(false);
                      setEditRoom(null);
                      setFormData({});
                      setGallery([]);
                      setPlans([]);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          ) : loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
              <Spinner animation="border" />
            </div>
          ) : (
            // 🔹 Rooms List
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Room</th>
                    <th>Quantity</th>
                    <th>Approved</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRooms.length > 0 ? (
                    paginatedRooms.map((item, i) => (
                      <tr key={item.id}>
                        <td>{currentPage * itemsPerPage + i + 1}</td>
                        <td>
                          {item.images?.[0] && (
                            <img
                              src={item.images[0].url || item.images[0]}
                              alt={item.images[0].alt || `${item.name}-image`}
                              style={{ width: "60px",height: "40px",  borderRadius: "6px" }}
                            />
                          )}
                        </td>
                        <td>{item.name}</td>
                        <td>{item.totalRoom}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              style={{ cursor: "pointer" }}
                              checked={item.isApproved}
                              onChange={() => handleToggle(item)}
                            />
                          </div>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle variant="link">&#x2022;&#x2022;&#x2022;</Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => handleEditRoom(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleDeleteRoom(item.id)}>
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No rooms found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && !showForm && filteredRooms.length > 0 && (
            <div className="d-flex justify-content-between mt-3">
              <div>
                Showing {currentPage * itemsPerPage + 1} to{" "}
                {Math.min((currentPage + 1) * itemsPerPage, filteredRooms.length)} of{" "}
                {filteredRooms.length} entries
              </div>
              <div>
                <Button
                  size="sm"
                   className="btn btn-sm btn-outline-primary me-2"
                  variant="outline-primary"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Prev
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    className={`ms-1 ${currentPage === i ? "me-2 btn-primary text-white" : "btn-outline-primary"}`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="btn btn-sm btn-outline-primary ms-1"
                  disabled={currentPage === totalPages - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default ResortRooms